import { ReactNode, useEffect, useRef, useState } from 'react';
import {
    BreakpointRangeProps,
    Button,
    Container,
    Dialog,
    IconName,
    SelectionCardGroup,
    withBreakpointRange,
} from '@optimus-web/core';
import { getTemplateColumnsGridSize } from '../utils/getTemplateColumnsGridSize';
import { getLanguageCardOptions } from './utils/getLanguageCardOptions';
import { getLanguagesSorted } from './utils/getLanguagesSorted';

export interface Language {
    code: string;
    fallbackLanguageCode?: string | null;
    englishName: string;
    localName: string;
}

interface BaseLanguageSelectorProps extends BreakpointRangeProps {
    languages: Language[];
    selectedLanguageCode: string;
    onChange: (languageCode: string) => void;
    dataTestId: string;
    portalContainerId: string;
    buttonAriaLabelText: string;
    dialogTitleText: ReactNode;
}

const BaseLanguageSelectDialog = ({
    languages,
    selectedLanguageCode,
    onChange,
    portalContainerId,
    dataTestId,
    breakpointRange,
    buttonAriaLabelText,
    dialogTitleText,
}: BaseLanguageSelectorProps) => {
    const [isDialogActive, setIsDialogActive] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dialogContentRef = useRef<HTMLDivElement>(null);

    const selectedLanguage = languages.find((language) => language.code === selectedLanguageCode);
    const languagesSorted = getLanguagesSorted({ languages, selectedLanguage });
    const languageCardOptions = getLanguageCardOptions({ languagesSorted });

    useEffect(() => {
        if (isDialogActive) {
            window.setTimeout(() => {
                const firstCheckboxEl = dialogContentRef.current?.querySelector('button[role="checkbox"]');
                if (firstCheckboxEl instanceof HTMLButtonElement) firstCheckboxEl.focus();
            }, 150);
        }
    }, [isDialogActive]);

    const focusButton = () => buttonRef.current?.focus();

    const handleChange = (languageCode: string | undefined) => {
        // For SelectionCardGroup we are using type="checkbox" so we need to prevent the user from unselecting the value
        if (languageCode !== undefined) {
            onChange(languageCode);
            setIsDialogActive(false);
            focusButton();
        }
    };

    const handleClose = () => {
        setIsDialogActive(false);
        focusButton();
    };

    return (
        <>
            <Button
                aria-label={buttonAriaLabelText}
                ref={buttonRef}
                data-test-id={dataTestId}
                onClick={() => setIsDialogActive(true)}
                icon={IconName.LanguageSwitch}
                variant="ghost"
                type="button"
            >
                {selectedLanguage?.localName}
            </Button>
            <Dialog
                active={isDialogActive}
                modal
                onClose={handleClose}
                size="extraLarge"
                title={dialogTitleText}
                portalContainerId={portalContainerId}
                contentRef={dialogContentRef}
            >
                <Container maxHeight="600px" padding={['50', '0', '0']}>
                    <SelectionCardGroup
                        inputValue={[selectedLanguageCode]}
                        onChange={(e_, newValue) => handleChange(newValue[1])}
                        options={languageCardOptions}
                        showSelector={false}
                        templateColumns={getTemplateColumnsGridSize(breakpointRange)}
                        type="checkbox"
                    />
                </Container>
            </Dialog>
        </>
    );
};

export const LanguageSelectDialog = withBreakpointRange<BaseLanguageSelectorProps>(BaseLanguageSelectDialog);
